import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  HelpCircle, Plus, Pencil, Trash2, Upload, X, Eye,
  Monitor, Smartphone, GripVertical, Save, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Erste Schritte',
  'MHD-Kontrolle',
  'Backshop',
  'Aufgaben',
  'Temperatur',
  'Bestellungen',
  'Lotto',
  'Mitarbeiter',
  'Statistik & Exports',
  'Konto & Abonnement',
  'Technik & Einstellungen',
  'Sonstiges',
];

const EMPTY_FORM = {
  title: '',
  category: '',
  content: '',
  order_index: 0,
  image_desktop_url: '',
  image_mobile_url: '',
};

export const SuperadminHelpCenter = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [deletingArticle, setDeletingArticle] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => { fetchArticles(); }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/help/articles`);
      setArticles(res.data || []);
    } catch {
      toast.error('Fehler beim Laden der Artikel');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingArticle(null);
    setForm({ ...EMPTY_FORM, order_index: articles.length });
    setPreviewMode(false);
    setDialogOpen(true);
  };

  const openEdit = (article) => {
    setEditingArticle(article);
    setForm({
      title: article.title || '',
      category: article.category || '',
      content: article.content || '',
      order_index: article.order_index ?? 0,
      image_desktop_url: article.image_desktop_url || '',
      image_mobile_url: article.image_mobile_url || '',
    });
    setPreviewMode(false);
    setDialogOpen(true);
  };

  const handleImageUpload = (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max. 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, [slot]: reader.result }));
      toast.success(slot === 'image_desktop_url' ? 'Desktop-Bild hochgeladen' : 'Mobile-Bild hochgeladen');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.category || !form.content.trim()) {
      toast.error('Titel, Kategorie und Inhalt sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/help/articles/${editingArticle.id}`, form);
        toast.success('Artikel aktualisiert');
      } else {
        await axios.post(`${API}/help/articles`, form);
        toast.success('Artikel erstellt');
      }
      setDialogOpen(false);
      fetchArticles();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingArticle) return;
    try {
      await axios.delete(`${API}/help/articles/${deletingArticle.id}`);
      toast.success('Artikel gelöscht');
      setDeleteDialogOpen(false);
      setDeletingArticle(null);
      fetchArticles();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  // Group articles by category
  const grouped = CATEGORIES.reduce((acc, cat) => {
    const items = articles.filter(a => a.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});
  const otherCategory = articles.filter(a => !CATEGORIES.includes(a.category));
  if (otherCategory.length > 0) grouped['Sonstiges (unkategorisiert)'] = otherCategory;

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <HelpCircle className="h-8 w-8 text-primary" />
              Hilfe-Verwaltung
            </h1>
            <p className="text-muted-foreground mt-1">
              Erstelle und verwalte Tutorial-Artikel für deine Kunden
            </p>
          </div>
          <Button onClick={openCreate} data-testid="create-article-btn" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Neuer Artikel
          </Button>
        </div>

        {/* Stats bar */}
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{articles.length}</span> Artikel gesamt
          <span>·</span>
          <span className="font-medium text-foreground">{Object.keys(grouped).length}</span> Kategorien
        </div>

        {/* Articles list grouped by category */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            Lade Artikel...
          </div>
        ) : articles.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <HelpCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Noch keine Artikel vorhanden.</p>
              <Button onClick={openCreate} className="mt-4" variant="outline">
                <Plus className="h-4 w-4 mr-2" /> Ersten Artikel erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {Object.entries(grouped).map(([category, items]) => (
              <Card key={category} className="overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition-colors"
                  onClick={() => toggleCategory(category)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{category}</span>
                    <Badge variant="secondary">{items.length}</Badge>
                  </div>
                  {expandedCategories[category] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {expandedCategories[category] && (
                  <div className="border-t divide-y">
                    {items.map(article => (
                      <div key={article.id} className="flex items-center gap-4 px-5 py-3" data-testid={`article-row-${article.id}`}>
                        <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{article.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {article.content?.substring(0, 80)}...
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {article.image_desktop_url && (
                            <Monitor className="h-3.5 w-3.5 text-blue-500" title="Desktop-Bild vorhanden" />
                          )}
                          {article.image_mobile_url && (
                            <Smartphone className="h-3.5 w-3.5 text-green-500" title="Mobile-Bild vorhanden" />
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openEdit(article)}
                            data-testid={`edit-article-${article.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setDeletingArticle(article); setDeleteDialogOpen(true); }}
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Artikel bearbeiten' : 'Neuer Artikel'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Title + Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Titel *</Label>
                <Input
                  data-testid="article-title-input"
                  placeholder="z.B. MHD-Kontrolle – Erste Schritte"
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Kategorie *</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="article-category-select">
                    <SelectValue placeholder="Kategorie wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Order index */}
            <div className="space-y-1.5 w-32">
              <Label>Reihenfolge</Label>
              <Input
                type="number"
                value={form.order_index}
                onChange={e => setForm(p => ({ ...p, order_index: parseInt(e.target.value) || 0 }))}
              />
            </div>

            {/* Content */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Inhalt (Markdown) *</Label>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPreviewMode(p => !p)}
                  className="text-xs gap-1"
                >
                  <Eye className="h-3.5 w-3.5" />
                  {previewMode ? 'Bearbeiten' : 'Vorschau'}
                </Button>
              </div>
              {previewMode ? (
                <div className="border rounded-md p-4 min-h-[200px] prose prose-sm max-w-none bg-muted/20">
                  <MarkdownPreview content={form.content} />
                </div>
              ) : (
                <Textarea
                  data-testid="article-content-textarea"
                  placeholder="**Fett**, *Kursiv*, ## Überschrift, - Aufzählung, [Link](https://...)"
                  value={form.content}
                  onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                  className="min-h-[200px] font-mono text-sm"
                />
              )}
              <p className="text-xs text-muted-foreground">Markdown wird unterstützt: **fett**, *kursiv*, ## Überschrift, - Liste</p>
            </div>

            {/* Images */}
            <div className="grid grid-cols-2 gap-4">
              {/* Desktop Image */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-blue-500" /> Desktop-Screenshot
                </Label>
                <input
                  type="file" accept="image/*" ref={desktopInputRef} className="hidden"
                  onChange={e => handleImageUpload(e, 'image_desktop_url')}
                />
                {form.image_desktop_url ? (
                  <div className="relative border rounded-md overflow-hidden">
                    <img src={form.image_desktop_url} alt="Desktop" className="w-full h-32 object-cover" />
                    <button
                      className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                      onClick={() => setForm(p => ({ ...p, image_desktop_url: '' }))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => desktopInputRef.current?.click()}
                    className="w-full h-24 border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    data-testid="upload-desktop-img"
                  >
                    <Upload className="h-5 w-5" />
                    <span className="text-xs">Desktop-Bild hochladen</span>
                  </button>
                )}
              </div>

              {/* Mobile Image */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-green-500" /> Mobile-Screenshot
                </Label>
                <input
                  type="file" accept="image/*" ref={mobileInputRef} className="hidden"
                  onChange={e => handleImageUpload(e, 'image_mobile_url')}
                />
                {form.image_mobile_url ? (
                  <div className="relative border rounded-md overflow-hidden">
                    <img src={form.image_mobile_url} alt="Mobile" className="w-full h-32 object-cover" />
                    <button
                      className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                      onClick={() => setForm(p => ({ ...p, image_mobile_url: '' }))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => mobileInputRef.current?.click()}
                    className="w-full h-24 border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    data-testid="upload-mobile-img"
                  >
                    <Upload className="h-5 w-5" />
                    <span className="text-xs">Mobile-Bild hochladen</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-article-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichern...' : (editingArticle ? 'Aktualisieren' : 'Erstellen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Artikel löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Möchtest du den Artikel <strong>"{deletingArticle?.title}"</strong> wirklich löschen?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-article-btn">
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Simple inline markdown preview component
const MarkdownPreview = ({ content }) => {
  const [ReactMarkdown, setReactMarkdown] = useState(null);
  const [remarkGfm, setRemarkGfm] = useState(null);

  useEffect(() => {
    Promise.all([
      import('react-markdown'),
      import('remark-gfm')
    ]).then(([md, gfm]) => {
      setReactMarkdown(() => md.default);
      setRemarkGfm(() => gfm.default);
    });
  }, []);

  if (!ReactMarkdown || !remarkGfm) return <p className="text-muted-foreground">Lade Vorschau...</p>;
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>;
};
